import { ReactElement } from 'react';
import { Box, Text, ListItem, List } from '@chakra-ui/react';
import { FeedbackForm } from 'components/common/FeedbackForm/FeedbackForm';
import { ContentType, HtmlTagNames } from '@novaheal/types';
import { ArticleSection } from 'components/Article/ArticleSection';
import { useIsLiked } from 'hooks/utils/useIsLiked';
import { MedicationInfo } from 'components/Medication/MedicationInfo';
import { useMedicationApi } from 'hooks/api/backend/useMedicationApi';
import { htmlToReactOptions } from 'utils/htmlToReactOptions';
import parse from 'html-react-parser';
import { Disclaimer } from 'components/Medication/Disclaimer';

type Props = {
  id: string;
};

export const Medication = ({ id }: Props): ReactElement => {
  const { data: medication } = useMedicationApi(id);
  const { isLiked, handleLikeChange } = useIsLiked(medication?.id, ContentType.MEDICATION);

  return (
    <Box
      width="100%"
      bg="background.primary"
      borderTopRadius="15px"
      display="flex"
      flexDirection="column"
      gap={{ base: '30px', md: '40px' }}
    >
      {medication && (
        <>
          <MedicationInfo
            name={medication.name}
            dosageFormIcons={medication.dosageFormIcons}
            substanceGroup={medication.substanceGroup}
            trademark={medication.trademark}
            handleLikeChange={() => handleLikeChange(medication.name)}
            isLiked={isLiked}
          />

          <Disclaimer />

          <Box>
            <Text
              textStyle="body.m.italic"
              color="text.secondary"
              px={{ base: 'l', md: '40px' }}
              mb="30px"
            >
              {medication.appetizer}
            </Text>

            <Box>
              {medication.contentSections.map((section) => {
                if (section) {
                  const hasVideo: boolean = section.content.includes(HtmlTagNames.VIDEO);
                  return (
                    <ArticleSection title={section.title} key={section.title} hasVideo={hasVideo}>
                      {parse(section.content, htmlToReactOptions)}
                    </ArticleSection>
                  );
                }
                return null;
              })}
            </Box>

            <ArticleSection title="Applikationsart">
              <List.Root as="ul">
                {medication.applicationType.map((type: string) => {
                  return <ListItem>{type}</ListItem>;
                })}
              </List.Root>
            </ArticleSection>

            <ArticleSection title="Literatur">
              {medication.literature.map((literatureEntry, index) => (
                <Text color="text.primary" key={literatureEntry} pb="5">
                  {`${index + 1}. ${literatureEntry}`}
                </Text>
              ))}
            </ArticleSection>

            <Box px={{ base: '10px', md: '40px' }} my="40px">
              <FeedbackForm
                type={ContentType.MEDICATION}
                title={medication.name}
                id={medication.id}
              />
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
};
